import {
  AddDogToSubscriptionInputSchema,
  AddDogToSubscriptionPreviewOutputSchema,
  DogBreedDto,
  DogDto,
  DogFoodAllergenDto,
  DogFoodTypeDto,
  DogHealthIssueDto,
  RecalculateDogMealPlanSubscriptionInputSchema,
  RecalculateDogMealPlanSubscriptionOutputSchema,
  UpdateDogBody,
} from '@lyka-pet-food/contracts'
import { initContract } from '@ts-rest/core'
import z from 'zod'

const c = initContract()
const AddDogToSubscriptionRequestSchema = AddDogToSubscriptionInputSchema.omit({
  userId: true,
  subscriptionId: true,
  workflowId: true,
})

export const animalsContract = c.router(
  {
    getDogs: {
      method: 'GET',
      path: '',
      summary: 'Get all dogs for the specified user',
      responses: {
        200: c.type<Array<DogDto>>(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    getDogsData: {
      method: 'GET',
      path: '/metadata',
      summary: 'Get dog breeds, health issues, food types, and allergens',
      responses: {
        200: c.type<{
          breeds: DogBreedDto[]
          healthIssues: DogHealthIssueDto[]
          foodTypes: DogFoodTypeDto[]
          foodAllergens: DogFoodAllergenDto[]
        }>(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    recalculateDogMealPlanSubscription: {
      method: 'POST',
      path: '/meal-plan/recalculate',
      summary: 'Recalculate recommended meal plan for a new dog',
      body: RecalculateDogMealPlanSubscriptionInputSchema,
      responses: {
        200: RecalculateDogMealPlanSubscriptionOutputSchema,
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    previewNewSubscription: {
      method: 'POST',
      path: '/meal-plan/preview',
      summary: 'Preview subscription updates when adding a dog (preferredMeals expects Meal enum slugs)',
      body: AddDogToSubscriptionRequestSchema,
      responses: {
        200: AddDogToSubscriptionPreviewOutputSchema,
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    addDogToSubscription: {
      method: 'POST',
      path: '/meal-plan/add',
      summary: 'Add dog to subscription via orchestration workflow (preferredMeals expects Meal enum slugs)',
      body: AddDogToSubscriptionRequestSchema,
      responses: {
        201: c.noBody(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    updateDog: {
      method: 'PATCH',
      path: '/:id',
      summary: 'Update a dog by ID',
      body: z.object({
        dog: z.custom<UpdateDogBody>(),
      }),
      responses: {
        200: c.type<DogDto>(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    createAnimalImage: {
      method: 'POST',
      path: '/:animalId/image',
      summary: 'Set or update the image for a dog',
      pathParams: z.object({
        animalId: z.string(),
      }),
      body: z.object({
        url: z.string(),
      }),
      responses: {
        204: c.noBody(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    removeAnimalImage: {
      method: 'DELETE',
      path: '/:animalId/image',
      summary: 'Remove the image for a dog',
      pathParams: z.object({
        animalId: z.string(),
      }),
      body: c.noBody(),
      responses: {
        204: c.noBody(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
    getPresignedPost: {
      method: 'POST',
      path: '/:id/presigned-post',
      summary: 'Get a presigned POST URL and fields for uploading a profile photo for this dog',
      body: z
        .object({
          extension: z
            .enum(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'])
            .optional()
            .describe('Allowed image types'),
        })
        .optional(),
      responses: {
        200: c.type<{ url: string; fields: Record<string, string>; key: string }>(),
        400: c.type<{ error: string }>(),
        401: c.type<{ error: string }>(),
        403: c.type<{ error: string }>(),
        500: c.type<{ error: string }>(),
      },
    },
  },
  {
    pathPrefix: '/users/me/dogs',
  },
)
